// src/features/task/TaskSubtasks.tsx
// Subtasks section for the task detail page: lists tasks whose parent is this
// task (ListTasksByParent), shows a progress summary, and supports quick-add
// (CreateTask with parent = this task, type SUBTASK). Uses the raw taskApi
// directly rather than the shared TaskStore — the store's ListXxx methods all
// write into the same observable `items` array, which would collide with
// TaskDependencies' own list lookups if both used the store simultaneously.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
} from '@react/components';
import { taskApi } from '@core/api';
import type { Task } from '@core/types/task';
import {
  getStatusMapping,
  taskStatusMap,
  formatEnumLabel,
  getDisplayName,
  getEntityId,
} from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';

interface TaskSubtasksProps {
  parentId: string;
  parentTypeName?: string;
  editable?: boolean;
}

export const TaskSubtasks = observer(({ parentId, parentTypeName = 'task', editable = true }: TaskSubtasksProps) => {
  const { t } = useI18n();

  const [subtasks, setSubtasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await taskApi.ListTasksByParent(parentTypeName, parentId, {
        pagination: { limit: 50, cursor: '', direction: 'forward' },
        sort: [],
      } as any);
      setSubtasks(res?.tasks?.tasks || []);
    } catch (e) {
      setError(errMsg(e, t('task.subtasks.loadError')));
      setSubtasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (parentId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, parentTypeName]);

  const submit = async () => {
    const t2 = title.trim();
    if (!t2) return;
    setCreating(true);
    setError(null);
    try {
      await taskApi.CreateTask({
        title: t2,
        type: 'TASK_TYPE_SUBTASK',
        parent: { id: parentId, type_name: parentTypeName } as any,
      } as any);
      setTitle('');
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.subtasks.addError')));
    } finally {
      setCreating(false);
    }
  };

  const done = subtasks.filter((s: any) => (s.task?.status || s.status) === 'TASK_STATUS_DONE').length;
  const total = subtasks.length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('task.subtasks.title')}</CardTitle>
        {total > 0 && (
          <Badge variant="secondary" size="sm">{done}/{total}</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}

        {loading ? (
          <div className="h-12 animate-pulse rounded-md bg-muted" aria-busy="true" />
        ) : total === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.subtasks.empty')}</p>
        ) : (
          <div className="divide-y divide-border">
            {subtasks.map((s: any) => {
              const id = getEntityId(s);
              const status = s.task?.status || s.status || '';
              const assignee = s.task?.assignee || s.assignee;
              return (
                <div key={id} className="py-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {status && (
                      <Badge variant={getStatusMapping(taskStatusMap, status).variant} size="sm">
                        {formatEnumLabel(status, 'TASK_STATUS_')}
                      </Badge>
                    )}
                    <Link to={`/tasks/${id}`} className="text-sm text-foreground hover:text-primary hover:underline truncate">
                      {s.title || id.substring(0, 8)}
                    </Link>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {assignee ? getDisplayName(assignee) : t('task.unassigned')}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {editable && (
          <div className="flex gap-1.5">
            <input
              type="text"
              aria-label={t('task.subtasks.addPlaceholder')}
              placeholder={t('task.subtasks.addPlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
            <Button size="sm" variant="secondary" isLoading={creating} disabled={!title.trim()} onClick={submit}>
              {t('task.subtasks.add')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default TaskSubtasks;
